import { JSX, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuMapPin, LuSearch } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { Box, Flex, Text } from '@chakra-ui/react'

import { SelectOption } from 'components/form/Select/useSelect'
import { SelectWithSearch } from 'components/form/SelectWithSearch'
import { CardActionButton } from 'components/ui/ItemCard/styles'
import { Loader } from 'components/ui/Loader'
import { Tooltip } from 'components/ui/tooltip'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import theme from 'styles/theme'
import { tryParseAsNumber } from 'utils/parser'

import { CustomerSelectedLocation } from './customerSelectedLocation'

type Props = {
  selectedRegionId: number | undefined
  title: string
  onCustomerLocationSelected: (selectedLocation: CustomerSelectedLocation) => void
}

export const RegionsSectionHeader = ({ title, selectedRegionId, onCustomerLocationSelected }: Props): JSX.Element => {
  const { t } = useTranslation(['common'])
  const dispatch = useDispatch()
  const { customerInfo } = useCustomerInfo()

  const [isOpen, setIsOpen] = useState(false)
  const [locationLoading, setLocationLoading] = useState(false)
  const [pickedLocation, setPickedLocation] = useState(false)
  const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>()
  const [searchOptions, setSearchOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    setIsOpen(customerInfo.customer === undefined)
  }, [customerInfo])

  const handleSearchClick = () => {
    setSearchOptions(selectedOption ? [selectedOption] : [])
    setIsOpen(!isOpen)
  }

  const handlePinClick = () => {
    setLocationLoading(true)
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const { latitude, longitude } = coords
          onCustomerLocationSelected({
            label: '',
            latitude: latitude,
            longitude: longitude,
          })
          setPickedLocation(true)
          setIsOpen(false)
          setSelectedOption(undefined)
          setLocationLoading(false)
        },
        (error) => {
          console.log(`Error on getting location`, error)
          dispatch(displaySnackbar({ message: t('error'), isError: true }))
          setLocationLoading(false)
        },
        { maximumAge: 60000, timeout: 1000 },
      )
    } else {
      setLocationLoading(false)
    }
  }

  const handleSearchTermChange = async (search: string) => {
    if (!search) {
      setSearchOptions([])
      return
    }

    const response = await apiClient.searchLocations(search)
    if (response.success && response.data) {
      const options = response.data.map<SelectOption>((s) => ({
        label: s.text,
        value: `${s.lat},${s.long}`,
      }))
      setSearchOptions(options)
      return
    }

    setSearchOptions([])
  }

  const onInputSelectChange = (value: SelectOption) => {
    setSelectedOption(value)

    const parts = (value.value as string).split(',')

    onCustomerLocationSelected({
      label: value.label,
      latitude: tryParseAsNumber(parts?.[0]),
      longitude: tryParseAsNumber(parts?.[1]),
    })

    setPickedLocation(false)
  }

  return (
    <Box width="100%">
      <Flex width="100%" justifyContent="space-between">
        <Text>{title ?? ''}</Text>

        <Flex gridGap={2}>
          <Tooltip
            showArrow
            content={t('getCurrentLocation')}
            aria-label={t('getCurrentLocation')}
            positioning={{ placement: 'bottom' }}
          >
            <CardActionButton
              onClick={handlePinClick}
              backgroundColor={pickedLocation && !selectedRegionId ? theme.token('colors.green.a100') : undefined}
            >
              {locationLoading ? <Loader size="md" /> : <LuMapPin />}
            </CardActionButton>
          </Tooltip>

          <Tooltip
            showArrow
            content={t('searchForLocation')}
            aria-label={t('searchForLocation')}
            positioning={{ placement: 'bottom' }}
          >
            <CardActionButton
              onClick={handleSearchClick}
              backgroundColor={selectedOption && !selectedRegionId ? theme.token('colors.green.a100') : undefined}
            >
              <LuSearch />
            </CardActionButton>
          </Tooltip>
        </Flex>
      </Flex>

      {isOpen && (
        <Box marginTop="10px" marginBottom="20px">
          <Text color="black" marginBottom="5px">
            {t('searchInNorway')}
          </Text>

          <SelectWithSearch
            name="select"
            defaultOption={selectedOption}
            options={searchOptions}
            onSearchTermChange={handleSearchTermChange}
            onChange={onInputSelectChange}
            placeholder={t('placeholderByPostCode')}
          />
        </Box>
      )}
    </Box>
  )
}
